import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EscalationRuleBuilder = ({ language }) => {
  const [rules, setRules] = useState([
    {
      id: '1',
      name: 'Escalade standard',
      description: 'Séquence de rappels en 3 niveaux',
      isActive: true,
      levels: [
        { level: 1, daysAfterDue: 3, channel: 'email', templateId: 't1' },
        { level: 2, daysAfterDue: 7, channel: 'email', templateId: 't2' },
        { level: 3, daysAfterDue: 14, channel: 'email', templateId: 't3' }
      ]
    }
  ]);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const channelOptions = [
    { value: 'email', label: 'Email' },
    { value: 'dashboard', label: 'Tableau de bord' }
  ];

  const handleAddLevel = (ruleId) => {
    const rule = rules?.find(r => r?.id === ruleId);
    if (rule) {
      const newLevel = {
        level: rule?.levels?.length + 1,
        daysAfterDue: 0,
        channel: 'email',
        templateId: ''
      };
      const updatedRules = rules?.map(r => 
        r?.id === ruleId ? { ...r, levels: [...r?.levels, newLevel] } : r
      );
      setRules(updatedRules);
    }
  };

  const handleRemoveLevel = (ruleId, levelIndex) => {
    const updatedRules = rules?.map(r => {
      if (r?.id === ruleId) {
        const newLevels = r?.levels?.filter((_, i) => i !== levelIndex);
        const renumberedLevels = newLevels?.map((level, i) => ({ ...level, level: i + 1 }));
        return { ...r, levels: renumberedLevels };
      }
      return r;
    });
    setRules(updatedRules);
  };

  const handleLevelChange = (ruleId, levelIndex, field, value) => {
    const updatedRules = rules?.map(r => {
      if (r?.id === ruleId) {
        const newLevels = [...r?.levels];
        newLevels[levelIndex] = { ...newLevels?.[levelIndex], [field]: value };
        return { ...r, levels: newLevels };
      }
      return r;
    });
    setRules(updatedRules);
  };

  const handleToggleActive = (ruleId) => {
    const updatedRules = rules?.map(r => 
      r?.id === ruleId ? { ...r, isActive: !r?.isActive } : r
    );
    setRules(updatedRules);
  };

  const handleCreateRule = () => {
    const newRule = {
      id: Date.now()?.toString(),
      name: 'Nouvelle règle',
      description: '',
      isActive: true,
      levels: [
        { level: 1, daysAfterDue: 3, channel: 'email', templateId: '' }
      ]
    };
    setRules([...rules, newRule]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Règles d'escalade
          </h2>
          <p className="text-sm text-muted-foreground">
            Configurez les séquences de rappels automatiques
          </p>
        </div>
        <Button onClick={handleCreateRule}>
          <Icon name="Plus" size={18} className="mr-2" />
          Nouvelle règle
        </Button>
      </div>

      {/* Rules List */}
      <div className="space-y-4">
        {rules?.map((rule) => (
          <div key={rule?.id} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-foreground">{rule?.name}</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rule?.isActive}
                      onChange={() => handleToggleActive(rule?.id)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
                <p className="text-sm text-muted-foreground">{rule?.description}</p>
              </div>
            </div>

            {/* Escalation Levels */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="TrendingUp" size={18} className="text-primary" />
                <h4 className="text-sm font-semibold text-foreground">
                  Niveaux d'escalade
                </h4>
              </div>

              {rule?.levels?.map((level, index) => (
                <div key={index} className="bg-muted/30 border border-border rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary font-bold">
                      {level?.level}
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Input
                        label="Jours après échéance"
                        type="number"
                        min="0"
                        value={level?.daysAfterDue}
                        onChange={(e) => handleLevelChange(rule?.id, index, 'daysAfterDue', parseInt(e?.target?.value))}
                      />
                      <Select
                        label="Canal"
                        options={channelOptions}
                        value={level?.channel}
                        onChange={(value) => handleLevelChange(rule?.id, index, 'channel', value)}
                      />
                      <Input
                        label="Template ID"
                        value={level?.templateId}
                        onChange={(e) => handleLevelChange(rule?.id, index, 'templateId', e?.target?.value)}
                        placeholder="t1, t2, t3..."
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveLevel(rule?.id, index)}
                      disabled={rule?.levels?.length === 1}
                    >
                      <Icon name="Trash2" size={18} className="text-error" />
                    </Button>
                  </div>
                </div>
              ))}

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddLevel(rule?.id)}
                fullWidth
              >
                <Icon name="Plus" size={16} className="mr-2" />
                Ajouter un niveau
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EscalationRuleBuilder;